import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styles from './OnboardingRolesQuestionsView.module.scss';
import MetaTags from '../../components/seo/MetaTags/MetaTags';
import OnboardingTemplate from '../../containers/templates/OnboardingTemplate/OnboardingTemplate';
import Sidebar from '../../containers/templates/OnboardingTemplate/_parts/Sidebar/Sidebar';
import { ReactComponent as ProfileIcon } from '../../asset/images/icons/profile.svg';
import { ReactComponent as RocketIcon } from '../../asset/images/icons/rocket.svg';
import { ReactComponent as PersonalDetailsIcon } from '../../asset/images/onboarding_complete_profile_icons/personal_details.svg';
import { ReactComponent as BusinessTypeIcon } from '../../asset/images/onboarding_complete_profile_icons/business_type.svg';
import { ReactComponent as IndustryIcon } from '../../asset/images/onboarding_complete_profile_icons/industry.svg';
import { ReactComponent as AnnualRevenueIcon } from '../../asset/images/onboarding_complete_profile_icons/annual_revenue.svg';
import { ReactComponent as FundingStageIcon } from '../../asset/images/onboarding_complete_profile_icons/funding_stage.svg';
import { ReactComponent as GrowthGoalsIcon } from '../../asset/images/onboarding_complete_profile_icons/growth_goals.svg';
import { ReactComponent as SpecialtiesIcon } from '../../asset/images/onboarding_complete_profile_icons/specialties.svg';
import { ReactComponent as DisciplinesPracticedIcon } from '../../asset/images/onboarding_complete_profile_icons/disciplines_practiced.svg';
import { ReactComponent as DisciplinesConsultedIcon } from '../../asset/images/onboarding_complete_profile_icons/disciplines_consulted.svg';
import { ReactComponent as AssetsIcon } from '../../asset/images/onboarding_complete_profile_icons/assets.svg';
import { ReactComponent as GeographicPreferencesIcon } from '../../asset/images/onboarding_complete_profile_icons/geographic_preferences.svg';
import { localStorageGetItem } from '../../utils/webStorage/localStorageMethods';
import { LOCAL_STORAGE_KEYS } from '../../enums/localStorageKeys';
import api from '../../api';
import { Navigate, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { ApiResponseDTO } from '../../dto/api';
import RoutesEnum from '../../enums/routes';
import CompleteProfileTemplate from '../../containers/templates/CompleteProfileTemplate/CompleteProfileTemplate';
import MainButton from '../../components/buttons/MainButton/MainButton';
import classNames from 'classnames';
import FancyBox from '../../components/blocks/FancyBox/FancyBox';

interface OnboardingRolesQuestionsViewProps {}

const StepTitleToIconsMap: Record<string, React.ReactElement> = {
  'Personal Details': <PersonalDetailsIcon />,
  'Business Type': <BusinessTypeIcon />,
  ['Industry']: <IndustryIcon />,
  'Annual Revenue': <AnnualRevenueIcon />,
  'Funding Stage': <FundingStageIcon />,
  'Growth Goals': <GrowthGoalsIcon />,
  ['Specialities']: <SpecialtiesIcon />,
  'Disciplines Practiced': <DisciplinesPracticedIcon />,
  ['Assets']: <AssetsIcon />,
  'Geographic Pref.': <GeographicPreferencesIcon />,
};

const OnboardingRolesQuestionsView: FunctionComponent<OnboardingRolesQuestionsViewProps> = () => {
  const [formSchema, setFormSchema] = useState<Array<any>>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [bonusRf, setBonusRF] = useState<number>(0);
  const navigate = useNavigate();
  const childRef =
    useRef<{
      handleSubmitWrapper: any;
      setCurrentStep: any;
      changeSkipPopUpActiveState: any;
    }>(null);

  // GET STEPS
  useEffect(() => {
    const getOnboardingSteps = async () => {
      if (localStorageGetItem(LOCAL_STORAGE_KEYS.USER_ID)) {
        try {
          const response = await api.Onboarding.getSteps(localStorageGetItem(LOCAL_STORAGE_KEYS.USER_ID) as string);
          const responseData = response.data;

          if (responseData.success === true && responseData.data) {
            setFormSchema(responseData.data);
          } else {
            console.error('Error:', responseData.errors);
            navigate(`/${RoutesEnum.MANIFESTO}`);
          }
        } catch (error) {
          const errorObj = error as AxiosError<ApiResponseDTO>;
          const errorData = errorObj.response?.data;
          if (errorData?.errorMessage) {
            console.error(errorData.errorMessage);
          }
          console.error('Error: ', error);
          navigate(`/${RoutesEnum.MANIFESTO}`);
        }
      } else {
        navigate(`/${RoutesEnum.MANIFESTO}`);
      }
    };

    getOnboardingSteps();
  }, []);
  // GET STEPS END

  const handleStepChange = (index: number) => {
    childRef.current?.setCurrentStep(index + 1);
  };

  const sidebarItems = formSchema.map((item) => {
    return {
      title: item.stepTitle,
      icon: StepTitleToIconsMap[item.stepTitle] ? StepTitleToIconsMap[item.stepTitle] : <ProfileIcon />,
    };
  });

  const handleBonusChange = (value) => {
    setBonusRF(value);
  };

  return (
    <>
      <MetaTags title={`Onboarding - Complete your profile`} />

      <OnboardingTemplate
        sidebar={<Sidebar list={sidebarItems} activeItem={currentStep - 1} onItemClick={handleStepChange} />}
        footer={
          <div className={styles['footer']}>
            <div className={styles['footer__left-part']}>
              {currentStep > 1 && (
                <MainButton sizeType="medium" visualType="white" onClick={() => handleStepChange(currentStep - 2)}>
                  Back
                </MainButton>
              )}
            </div>
            <div className={styles['footer__right-part']}>
              <MainButton
                sizeType="medium"
                visualType="white"
                onClick={() => childRef.current?.changeSkipPopUpActiveState(true)}
              >
                Skip
              </MainButton>

              {formSchema.length === currentStep ? (
                <MainButton
                  sizeType="medium"
                  onClick={() => {
                    childRef?.current?.handleSubmitWrapper();
                  }}
                >
                  Submit
                </MainButton>
              ) : (
                <MainButton
                  sizeType="medium"
                  onClick={() => {
                    handleStepChange(currentStep);
                  }}
                >
                  Next
                </MainButton>
              )}
            </div>
          </div>
        }
      >
        <div className={styles['header']}>
          <p className={classNames(styles['title'], 'text text--h3')}>{formSchema[currentStep - 1]?.stepTitle}</p>
          <div className={styles['rf-box']}>
            <FancyBox
              title="Rocket Fuel earned"
              text={`$ ${bonusRf}`}
              icon={<RocketIcon style={{ width: '40px' }} />}
            />
          </div>
        </div>

        <CompleteProfileTemplate
          visualType="1"
          formSchemaProp={formSchema}
          showRfTipModalOnEnter={true}
          ref={childRef}
          onStepChange={setCurrentStep}
          onBonusChange={handleBonusChange}
          hideHeader
          hideFooter
          hideTitle
          hideSidebar
        />
      </OnboardingTemplate>
    </>
  );
};

export default OnboardingRolesQuestionsView;
